// react
import { useMemo } from 'react';

// types
import { ExchangeRateByDate } from '../../../@types';

// mui
import { Grid, InputAdornment, InputLabel } from '@mui/material';

// fields
import { Input, InputProps } from '../Input/Input';

// theme
import { theme } from '../../../theme';

// props
export interface AmountsConversionsInput extends Omit<InputProps, 'onChange'> {
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	value?: number | string;
}

export interface AmountsConversionsProps {
	currency: string;
	exchangeRates: ExchangeRateByDate[];
	inputs: AmountsConversionsInput[];
	label?: string;
}

export const AmountsConversions = ({
	currency,
	exchangeRates,
	inputs,
	label,
}: AmountsConversionsProps) => {
	// Calculate converted amounts for each input
	const convertedAmounts = useMemo(() => {
		return inputs.map((input) => {
			const baseAmount = Number(input.value) || 0;

			return exchangeRates.map((rate) => {
				if (!rate.exchangeRate || rate.exchangeRate === 0) {
					return {
						amount: 0,
						currency: rate.currency,
					};
				}

				const convertedAmount = (baseAmount / rate.exchangeRate).toFixed(2);

				return {
					amount: convertedAmount,
					currency: rate.currency,
				};
			});
		});
	}, [inputs, exchangeRates]);

	return (
		<Grid container spacing={0}>
			{label && (
				<Grid item xs={12}>
					<InputLabel required sx={{ mb: 0.5 }}>
						{label}
					</InputLabel>
				</Grid>
			)}
			<Grid item xs={12}>
				<Grid container spacing={1.5}>
					{/* Input and Conversion Rows */}
					{inputs.map((input, inputIndex) => (
						<Grid item xs={12} key={inputIndex}>
							<Grid container spacing={1.5} alignItems="center">
								{/* Base Amount Input */}
								<Grid item xs={12} md={true}>
									<Input {...input} fullWidth size="small" type="number" currency={currency} />
								</Grid>

								{/* Converted Amounts (Read-only) */}
								{convertedAmounts[inputIndex]?.map((converted, conversionIndex) => (
									<Grid item xs={12} md={true} key={conversionIndex}>
										<Input
											currency={converted.currency}
											fullWidth
											id={`${input.id}-conversion-${converted.currency}`}
											name={`${input.name}-conversion-${converted.currency}`}
											InputProps={{
												readOnly: true,
												startAdornment: <InputAdornment position="start">=</InputAdornment>,
											}}
											size="small"
											sx={{
												'& .MuiInputBase-input': {
													color: theme.palette.text.primary,
													cursor: 'default',
												},
											}}
											type="number"
											value={converted.amount}
										/>
									</Grid>
								))}
							</Grid>
						</Grid>
					))}
				</Grid>
			</Grid>
		</Grid>
	);
};
